import { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from "react-oidc-context"
import styles from './Dashboard.module.sass'
import AstrotekLogo from '../../assets/astrotek-text.svg'
import AddWalletButton from 'assets/add-wallet-button.svg'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Menu, MenuItem } from '@mui/material'
import Header from 'components/Header'
import ScrollToTop from 'components/ScrollToTop'
import TeaserOverlay from 'components/TeaserOverlay'
import UnlockWallet from 'components/UnlockWallet'
import TermsOfServiceModal from 'components/Modal/TermsOfService'
import dashboardRoutes from 'constants/dashboardRoutes'
import {
  getMembershipStatus,
  getNotificationData,
  getTopTokenPriceChange,
} from 'services/user'
import { setDuration, setShowAddWallet, setUser } from '../../redux/userReducer'
import { RootState } from 'redux/store'
import cn from 'classnames'
import CustomScrollbar from 'components/CustomScrollBar'


function DashboardLayout() {
  const auth = useAuth()
  const duration = useSelector((state: RootState) => state.user.dataDuration)
  const showAddWallet = useSelector((state: RootState) => state.user.showAddWallet)
  const wallets = useSelector((state: RootState) => state.user.wallets)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [firstVisit, setFirstVisit] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [navLinksOpen, setNavLinksOpen] = useState(false)
  const open = Boolean(anchorEl)

  // First visit and Terms check
  useEffect(() => {
    const firstVisit = sessionStorage.getItem('firstVisit')
    const termsAccepted = sessionStorage.getItem('termsAccepted')
    
    if (!firstVisit && firstVisit !== 'true') {
      setFirstVisit(true)
      sessionStorage.setItem('firstVisit', 'true')
    }
    
    if (!termsAccepted || termsAccepted !== 'true') {
      setShowTerms(true)
    }
  }, [])

  // Authentication check - simplified
  useEffect(() => {
    if (!auth.isAuthenticated) {
      auth.signinRedirect()
    } else if (auth.user) {
      dispatch(setUser({
        email: auth.user.profile.email,
        name: auth.user.profile.name,
      }))
    }
  }, [auth.isAuthenticated, auth.user])

  // Modified Terms check - add this one
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.profile) {
      const hasAcceptedToS = auth.user.profile['custom:tos_agreed'] === 'true' && 
                            auth.user.profile['custom:tos_agreed_date'] != null;
      
      setShowTerms(!hasAcceptedToS); // This will set it to false if they've accepted
    }
  }, [auth.isAuthenticated, auth.user?.profile]);
  

  // Get top token prices on initial load
  useEffect(() => {
    if (auth.user?.access_token) {
      getTopTokenPriceChange(auth.user.access_token)
    }
  }, [auth.user?.access_token])

  const refreshTokenData = useCallback(() => {
    if (auth.user?.access_token) {
      getTopTokenPriceChange(auth.user.access_token)
    }
  }, [auth.user?.access_token])

  // Membership status check
  useEffect(() => {
    if (auth.user?.profile.email && auth.user?.access_token) {
      getMembershipStatus(auth.user.profile.email, auth.user.access_token)
    }
  }, [auth.user?.profile.email, auth.user?.access_token])

  const currentRoute = dashboardRoutes.find((route) => {
    return (
      location.pathname.includes(route.id) ||
      (location.pathname === '/' && route.id === 'overview')
    )
  })

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (
    value: React.SetStateAction<string> | null,
    valInt: React.SetStateAction<number> | null
  ) => {
    if (value) {
      dispatch(setDuration({ id: valInt, value }))
    }
    setAnchorEl(null)
  }

  return (
    <div className={styles.container}>
      <ScrollToTop />
      <>
        <div
          className={cn(styles.navigation, {
            [styles.navLinksOpen]: navLinksOpen,
          })}
        >
          <div className={styles.logo}>
            <img src={AstrotekLogo} alt="Astrotek Logo" />
          </div>
          <div className={styles.navLinks}>
            <div className={styles.sectionTitle}>My Portfolio</div>
            {dashboardRoutes.map((route, index) => {
              const isSelected = currentRoute?.id === route.id
              const selectedNav = isSelected ? styles.selectedNav : ''
              const selectedPath = route.id === 'overview' ? '/' : '/' + route.id
              const showNebulaTitle = route.id === 'nebula-search'
  
              return (
                <>
                  {showNebulaTitle && <div className={styles.sectionTitle}>Search</div>}
                  <div
                    onClick={() => {
                      navigate(selectedPath)
                      dispatch(setShowAddWallet(false))
                      setNavLinksOpen(false)
                    }}
                    key={route.id}
                    className={cn(styles.navLink, selectedNav)}
                  >
                    <p>{route.name}</p>
                  </div>
                </>
              )
            })}
          </div>
  
          <Box textAlign="center" mt="auto">
            <div
              id="add-wallet-button"
              onClick={() => {
                dispatch(setShowAddWallet(true))
                setNavLinksOpen(false)
              }}
              className={styles.addNewWalletButton}
            >
              <img src={AddWalletButton} alt="Add Wallet" />
              <p>Set New Wallet</p>
            </div>
  
            <div className={styles.dropdown} onClick={(e) => handleClick(e as any)}>
              <div className={styles.duration}>{duration.value}</div>
              <ExpandMoreIcon />
            </div>
            <div
              className={styles.upgradeButton}
              onClick={(e) => {
                navigate('/billing')
                setNavLinksOpen(false)
              }}
            >
              Upgrade to PRO
            </div>
          </Box>
  
          <Menu
            id="dropdown-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={() => handleClose(null, null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            sx={{ marginTop: '5px', '.MuiMenu-list': { background: '#1B1B24' } }}
          >
            <>
              <MenuItem onClick={() => handleClose('Last 7 Days', 7)}>
                <div className={styles.menuItem}>Last 7 Days</div>
              </MenuItem>
              <MenuItem onClick={() => handleClose('Last 14 Days', 14)}>
                <div className={styles.menuItem}>Last 14 Days</div>
              </MenuItem>
              <MenuItem onClick={() => handleClose('Last 1 Month', 30)}>
                <div className={styles.menuItem}>Last 1 Month</div>
              </MenuItem>
              <MenuItem onClick={() => handleClose('Last 3 Month', 90)}>
                <div className={styles.menuItem}>Last 3 Month</div>
              </MenuItem>
            </>
          </Menu>
        </div>
  
        <div className={styles.contentContainer}>
          {/* <Header 
            navLinksOpen={navLinksOpen} 
            setNavLinksOpen={setNavLinksOpen} 
          /> */}
          <Header 
            navLinksOpen={navLinksOpen} 
            setNavLinksOpen={setNavLinksOpen}
            onRefreshTokens={refreshTokenData}
          />
          <div className={styles.content}>
            {showAddWallet ? (
              <CustomScrollbar>
                <div className={styles.walletContainer}>
                  <UnlockWallet
                    onUnlock={(walletAddress) => {
                      /* setWalletAddress(walletAddress) */
                    }}
                  />
                  <div
                    onClick={() => {
                      dispatch(setShowAddWallet(false))
                    }}
                    className="button-stroke"
                  >
                    Go to Dashboard
                  </div>
                </div>
              </CustomScrollbar>
            ) : (
              <Outlet />
            )}
  
            {firstVisit ? (
              <TeaserOverlay onCompleted={() => setFirstVisit(false)} />
            ) : null}

            {/* {showTerms && (
              <TermsOfServiceModal
                isOpen={showTerms}
                onClose={() => {
                  setShowTerms(false)
                }}
              />
            )} */}
            {showTerms && (
              <TermsOfServiceModal
                isOpen={true}
                onClose={() => setShowTerms(false)}
                onAccept={() => {
                  setShowTerms(false);
                }}
              />
            )}
          </div>
        </div>
      </>
    </div>
  )
}

export default DashboardLayout


























